import React, { useState, useMemo, useEffect } from 'react';

import styled from 'styled-components';

import { MerchProductColor } from '@services/merch/types/api/merch-product-color';

import MerchCard from '../merch-card';
import MerchImg from '../merch-img/index';

import Flex from '@components/layout/flex';
import SwiperCtrl from '@components/actions/swiper';
import Typography from '@components/basics/typography';

export type ImagesPosition = 'front' | 'back' | 'neck';
const ImagePositionsList = ['front', 'back', 'neck'];

interface MerchArtSliderProps {
  merchColors: MerchProductColor[];
  className?: string;
  colors?: string[];
  placeholderImage: string;
  sampleImages?: string[];
}

export const MerchArtSlider = ({
  className,
  merchColors = [],
  colors,
  placeholderImage,
  sampleImages,
}: MerchArtSliderProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activePosition, setActivePosition] = useState<ImagesPosition>('front');
  const [positions, setPositions] = useState<ImagesPosition[] | undefined>();

  useEffect(() => {
    const colorsPositions =
      merchColors[0]?.images?.reduce((acc: Record<string, boolean>, image) => {
        if (ImagePositionsList.includes(image.position)) {
          acc[image.position] = true;
        }
        return acc;
      }, {}) || {};
    const positionsList = Object.keys(colorsPositions);

    setPositions(positionsList as ImagesPosition[]);
  }, [merchColors]);

  const images = useMemo(() => {
    return merchColors[activeIndex]?.images
      ?.filter((image) => image.position === activePosition)
      .map((image) => ({
        ...image,
        src: `${image.src}?v=${new Date().toISOString()}`,
      }));
  }, [merchColors, activeIndex, activePosition]);

  const handleSelectPosition = (newPosition: ImagesPosition) => {
    if (newPosition !== activePosition) {
      setActivePosition(newPosition);
    }
  };

  const handleSelectColor = (color: string) => {
    console.log(color);
    console.log(merchColors);
    const newActiveIndex = merchColors.findIndex(
      (merchColor) => merchColor.color === color
    );
    if (newActiveIndex !== activeIndex) {
      setActiveIndex(newActiveIndex);
    }
  };

  return (
    <MerchArtSliderWrapper>
      <>
        {images?.length ? (
          images.length >= 2 ? (
            <SwiperCtrl
              key={`images-${activeIndex}-slides`}
              id="MerchArtSlider"
              noPagination
              hasArrows
              slidesLength={images?.length}
            >
              {images.map((image) => (
                <div key={`merch-product-swiper-${image.src}`}>
                  <MerchCard>
                    <MerchImg product={image.src} />
                  </MerchCard>
                </div>
              ))}
            </SwiperCtrl>
          ) : (
            <div
              className={className}
              key={`merch-product-swiper-${images[0]?.src}`}
            >
              <MerchCard>
                <MerchImg product={images[0]?.src} />
              </MerchCard>
            </div>
          )
        ) : sampleImages?.length ? (
          sampleImages.length >= 2 ? (
            <SwiperCtrl
              id="MerchArtSlider"
              key={`sampleImages-${activeIndex}-slides`}
              noPagination
              hasArrows
              slidesLength={sampleImages?.length}
            >
              {sampleImages.map((sampleImage) => (
                <div key={`merch-product-swiper-${sampleImage}`}>
                  <MerchCard>
                    <MerchImg product={sampleImage} />
                  </MerchCard>
                </div>
              ))}
            </SwiperCtrl>
          ) : (
            <div
              className={className}
              key={`merch-product-swiper-${sampleImages[0]}`}
            >
              <MerchCard>
                <MerchImg product={sampleImages[0]} />
              </MerchCard>
            </div>
          )
        ) : (
          <div
            className={className}
            key={`merch-product-placeholder-${placeholderImage}`}
          >
            <MerchCard>
              <MerchImg product={placeholderImage} />
            </MerchCard>
          </div>
        )}
      </>
      {!!merchColors.length && (
        <Flex justifyContent="space-around" alignItems="center" gap={10}>
          <ColorSelectWrapper
            justifyContent="center"
            alignItems="center"
            gap={10}
          >
            {merchColors?.map(
              ({ color }, index) =>
                !!color &&
                colors?.includes(color) &&
                index < 10 && (
                  <CircleColor
                    className={activeIndex === index ? 'active' : ''}
                    color={color}
                    key={color}
                    onClick={() => handleSelectColor(color)}
                  />
                )
            )}
          </ColorSelectWrapper>
          {!!positions?.length && positions?.length > 1 && (
            <Flex justifyContent="center" alignItems="center" gap={3}>
              {positions.map(
                (position) =>
                  position && (
                    <Position
                      key={position}
                      weight="700"
                      type="small"
                      capitalize
                      className={activePosition === position ? 'active' : ''}
                      onClick={() => handleSelectPosition(position)}
                    >
                      {position}
                    </Position>
                  )
              )}
            </Flex>
          )}
        </Flex>
      )}
    </MerchArtSliderWrapper>
  );
};

export default MerchArtSlider;

const MerchArtSliderWrapper = styled.div``;

const Position = styled(Typography)`
  cursor: pointer;
  padding: 10px;
  color: ${({ theme }) => theme.colors.tertiary};
  background-color: ${({ theme }) => theme.colors.differentBackground};

  &.active {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:first-child {
    border-radius: 10px 0 0 10px;
  }
  &:last-child {
    border-radius: 0 10px 10px 0;
  }
`;

const ColorSelectWrapper = styled(Flex)`
  padding: 10px 16px;
  border-radius: 10px;
  flex-wrap: wrap;
  background-color: ${({ theme }) => theme.colors.differentBackground};
`;

interface CircleColorProps {
  color: string;
}

const CircleColor = styled.div<CircleColorProps>`
  cursor: pointer;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid transparent;
  background-color: ${({ color }) => color};
  transition: border
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &.active {
    border-color: ${({ theme }) => theme.colors.blueColor};
  }
`;
